import { Persona } from "@moe/priv/model/persona";
import { AnimatedList } from "@web/components/CardList";
import { PersonaItem } from "@web/components/persona/PersonaItem";
import { useDeletePersona } from "@web/components/persona/use-delete-persona";
import { useUpdatePersona } from "@web/components/persona/use-update-persona";
import { PersonaFormModal } from "@web/components/PersonaFormModal";
import { useAppContext } from "@web/route-services/root/AppContext";
import { AnimatePresence, motion } from "motion/react";
import { useRef } from "react";

interface Props {
  personas: Persona[];
  isPending: boolean;
  onClick?: (persona: Persona) => void;
}

/**
 * The PersonaList component displays a scrollable list of personas with loading and empty states.
 * It also provides functionality to take actions on each persona item, such as editing or deleting.
 *
 * @param personas - Array of persona objects to display
 * @param isPending - Loading state boolean
 * @param onClick - Optional callback function to handle persona item click
 */
export function PersonaList({ personas, isPending, onClick }: Props) {
  const listRef = useRef<HTMLDivElement>(null);
  const { createModal, createDialog } = useAppContext();

  const updatePersona = useUpdatePersona();
  const deletePersona = useDeletePersona();

  const handleEdit = (persona: Persona) => {
    createModal({
      content: (
        <PersonaFormModal
          title="Edit Persona"
          persona={persona}
          submit={{
            label: "Save",
            handle: async (formData) => {
              const updateData = {
                id: persona.id,
                name: formData.name,
                description: formData.description,
                avatar: formData.avatar,
                isDefault: formData.isDefault
              };
              updatePersona.mutate(updateData);
            }
          }}
          remove={{
            label: "Remove",
            handle: async () => {
              deletePersona.mutate(persona);
            }
          }}
        />
      )
    });
  };

  const handleDelete = (persona: Persona) => {
    createDialog({
      title: "Confirm Deletion",
      description: "Are you sure you want to delete this greeting?",
      actionLabel: "Delete",
      onAction: async () => deletePersona.mutate(persona)
    });
  };

  return (
    <div ref={listRef} className="scroll-secondary flex w-full flex-col space-y-2 overflow-y-auto px-3">
      <AnimatePresence mode="wait">
        {isPending ? (
          // Loading state
          <motion.div
            key="loading"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="flex h-full w-full items-center justify-center"
          >
            <p className="text-tx-tertiary select-none text-center text-sm font-[650]">Loading personas...</p>
          </motion.div>
        ) : personas.length === 0 ? (
          // Empty state
          <motion.div
            key="empty"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="flex h-full w-full items-center justify-center"
          >
            <p className="text-tx-tertiary select-none text-center text-sm font-[650]">
              {"You don't have a persona yet..."}
            </p>
          </motion.div>
        ) : (
          // Populated list state
          <AnimatedList
            key="list"
            items={personas}
            renderItem={(persona) => (
              <PersonaItem persona={persona} onEdit={handleEdit} onDelete={handleDelete} onClick={onClick} />
            )}
            itemKeyFn={(persona) => persona.id}
          />
        )}
      </AnimatePresence>
    </div>
  );
}
